import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Grid, Card, Stack, Typography, Switch, TextField, Button, IconButton, Chip,
  MenuItem, alpha, useTheme, Snackbar, Alert, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Checkbox,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip,
  CircularProgress, Divider,
} from '@mui/material';
import {
  Zap, MessageSquare, Clock, Plus, Trash2, Bot, Workflow, GitBranch, Rocket, Pencil, X,
  CheckCircle2, Sparkles, Play, Send, RefreshCw, ShieldCheck,
} from 'lucide-react';
import { SEED_QUICK_REPLIES, SEED_KEYWORD_RULES } from './waData';
import WaSectionHeader, { waGridItem } from './WaLayout';
import WaMessageEditor, { renderRichText } from './WaMessageEditor';
import WhatsAppFlowBuilder from './WhatsAppFlowBuilder';
import WaReplyButtonsEditor from './WaReplyButtonsEditor';
import { waApi } from './waApi';

// Default thank-you sent after a contact picks a call slot. {{time}}/{{slot}} →
// the chosen slot; {{user}}/{{name}} → the contact's name.
const DEFAULT_CALL_CONFIRM = 'Thank you! ✅ Your call is scheduled for *{{time}}* and Rajkumar will assist you. Talk soon! 🙌';

const ToggleCard = ({ icon: Icon, color, title, desc, enabled, onToggle, children }) => {
  return (
    <Card sx={{ p: 2 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: alpha(color, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={19} color={color} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: 14, fontWeight: 800 }}>{title}</Typography>
            <Switch checked={enabled} onChange={onToggle} />
          </Stack>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>{desc}</Typography>
          {enabled && children && <Box sx={{ mt: 1.5 }}>{children}</Box>}
        </Box>
      </Stack>
    </Card>
  );
};

const WhatsAppAutomation = () => {
  const theme = useTheme();
  const [subTab, setSubTab] = useState(0);
  const [templates, setTemplates] = useState([]);
  const approved = templates.filter((t) => t.status === 'APPROVED');
  const [greeting, setGreeting] = useState({ on: true, trigger: 'new', days: 14, text: 'Hi! Thanks for messaging Reseller Bazzar. How can we help you today?', buttons: [] });
  const [away, setAway] = useState({ on: true, text: 'We are away right now. Our team replies between 9 AM and 7 PM IST.', buttons: [] });
  const [renewal, setRenewal] = useState({ on: false, template: '' });
  const [quickReplies, setQuickReplies] = useState(SEED_QUICK_REPLIES);
  const [newQr, setNewQr] = useState('');
  const [editingQrIndex, setEditingQrIndex] = useState(null);
  const [rules, setRules] = useState([]);
  // Enhanced keyword rule state with match mode, anti-fatigue cooldown, and action tags
  const [newRule, setNewRule] = useState({
    keywords: '',
    reply: '',
    buttons: [],
    match_mode: 'word',
    cooldown_hours: 12,
    action_tags: '',
  });
  // Id of the rule currently being edited; null when adding a new rule.
  const [editingId, setEditingId] = useState(null);
  // Customer lifecycle funnel (Open → Call Pending → Follow Up + account tags).
  // slots is held as a comma-separated string in the UI; saved as an array.
  const [lifecycle, setLifecycle] = useState({ on: false, days: 2, slots: '11am, 4pm', text: 'When would you like us to call you?', confirmText: DEFAULT_CALL_CONFIRM });
  // WhatsApp Follow-up & Onboarding Automation state
  const [onboarding, setOnboarding] = useState({
    on: false,
    cooldownDays: 2,
    maxNudgesPerStage: 3,
    maxPerRun: 50,
    fatigueWindowHours: 24,
    unverifiedTemplate: '',
    businessTemplate: '',
    productsTemplate: '',
    planTemplate: '',
  });

  // Live audience & logs state
  const [autoStatus, setAutoStatus] = useState({
    enabled: false,
    counts: { unverified: 0, business_pending: 0, product_pending: 0, total: 0 },
    performance_30d: { sent: 0, simulated: 0, skipped: 0, failed: 0 },
    is_live_send: false,
  });
  const [autoLogs, setAutoLogs] = useState([]);
  const [loadingAutoStatus, setLoadingAutoStatus] = useState(false);

  // Manual run campaign modal
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [runSegment, setRunSegment] = useState('all');
  const [runDryRun, setRunDryRun] = useState(false);
  const [runLimit, setRunLimit] = useState(50);
  const [runningAuto, setRunningAuto] = useState(false);
  const [runReport, setRunReport] = useState(null);

  // Test single number modal
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testTemplate, setTestTemplate] = useState('');
  const [testSegment, setTestSegment] = useState('business_pending');
  const [testSampleName, setTestSampleName] = useState('Rahul Sharma');
  const [testingMsg, setTestingMsg] = useState(false);
  const [testFeedback, setTestFeedback] = useState(null);

  // Stateful, button-driven onboarding flows (waSetting.flows). Managed as an
  // array; the builder edits the first flow.
  const [flows, setFlows] = useState([]);
  // Keyword intelligence & unmatched queries state
  const [keywordStats, setKeywordStats] = useState({ totals: {}, topKeywords: [] });
  const [unmatchedQueries, setUnmatchedQueries] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [toast, setToast] = useState('');

  // Dirty tracking: compare a JSON snapshot of the editable state to the last
  // saved baseline so "Save Changes" is only enabled when there's something to save.
  const baselineRef = useRef('');
  const [loaded, setLoaded] = useState(false);
  const snapshot = JSON.stringify({ greeting, away, renewal, quickReplies, rules, lifecycle, onboarding, flows });
  const dirty = loaded && snapshot !== baselineRef.current;

  // Load approved templates (for the renewal reminder) + saved automation settings.
  useEffect(() => {
    waApi.getTemplates().then((rows) => { if (Array.isArray(rows)) setTemplates(rows); }).catch(() => { });

    waApi.getSettings().then(({ settings }) => {
      const next = {
        greeting: { on: true, trigger: 'new', days: 14, text: 'Hi! Thanks for messaging Reseller Bazzar. How can we help you today?', buttons: [] },
        away: { on: true, text: 'We are away right now. Our team replies between 9 AM and 7 PM IST.', buttons: [] },
        renewal: { on: false, template: '' },
        quickReplies: SEED_QUICK_REPLIES,
        rules: SEED_KEYWORD_RULES,
        lifecycle: { on: false, days: 2, slots: '11am, 4pm', text: 'When would you like us to call you?', confirmText: DEFAULT_CALL_CONFIRM },
        onboarding: { on: false, cooldownDays: 2, maxPerRun: 50, unverifiedTemplate: '', businessTemplate: '', productsTemplate: '', planTemplate: '' },
        flows: [],
      };
      if (settings) {
        if (settings.greeting) next.greeting = { on: settings.greeting.enabled, trigger: settings.greeting.trigger || 'new', days: settings.greeting.days || 14, text: settings.greeting.text || '', buttons: Array.isArray(settings.greeting.buttons) ? settings.greeting.buttons : [] };
        if (settings.away) next.away = { on: settings.away.enabled, text: settings.away.text || '', buttons: Array.isArray(settings.away.buttons) ? settings.away.buttons : [] };
        if (settings.renewal) next.renewal = { on: settings.renewal.enabled, template: settings.renewal.template || '' };
        if (settings.quick_replies !== undefined && Array.isArray(settings.quick_replies)) next.quickReplies = settings.quick_replies;
        if (settings.keyword_rules !== undefined && Array.isArray(settings.keyword_rules)) next.rules = settings.keyword_rules.map((r, i) => ({
          id: r._id || `r${i}`,
          keywords: Array.isArray(r.keywords) && r.keywords.length ? r.keywords : (r.keyword ? [r.keyword] : []),
          reply: r.reply || '',
          buttons: Array.isArray(r.buttons) ? r.buttons : [],
          match_mode: r.match_mode || 'word',
          cooldown_hours: r.cooldown_hours ?? 12,
          action_tags: Array.isArray(r.action_tags) ? r.action_tags : [],
          hit_count: r.hit_count || 0,
          last_hit_at: r.last_hit_at || null,
          active: r.active !== false,
        }));
        if (settings.lifecycle) next.lifecycle = {
          on: Boolean(settings.lifecycle.enabled),
          days: settings.lifecycle.follow_up_after_days ?? 2,
          slots: (settings.lifecycle.call_slots || ['11am', '4pm']).join(', '),
          text: settings.lifecycle.call_schedule_text || 'When would you like us to call you?',
          confirmText: settings.lifecycle.call_confirm_text ?? DEFAULT_CALL_CONFIRM,
        };
        if (settings.onboarding) next.onboarding = {
          on: Boolean(settings.onboarding.enabled),
          cooldownDays: settings.onboarding.cooldown_days ?? 2,
          maxNudgesPerStage: settings.onboarding.max_nudges_per_stage ?? 3,
          maxPerRun: settings.onboarding.max_per_run ?? 50,
          fatigueWindowHours: settings.onboarding.fatigue_window_hours ?? 24,
          unverifiedTemplate: settings.onboarding.unverified_template || '',
          businessTemplate: settings.onboarding.business_template || '',
          productsTemplate: settings.onboarding.products_template || '',
          planTemplate: settings.onboarding.plan_template || '',
        };
        if (Array.isArray(settings.flows)) next.flows = settings.flows;
      }
      setGreeting(next.greeting); setAway(next.away); setRenewal(next.renewal);
      setQuickReplies(next.quickReplies); setRules(next.rules); setLifecycle(next.lifecycle); setOnboarding(next.onboarding); setFlows(next.flows);
      baselineRef.current = JSON.stringify(next);
      setLoaded(true);
    }).catch(() => {
      // Backend unreachable — baseline is the current defaults, so nothing reads as dirty.
      baselineRef.current = snapshot;
      setLoaded(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadKeywordIntelligence = async () => {
    setAnalyticsLoading(true);
    try {
      const [analyticsData, unmatchedData] = await Promise.all([
        waApi.getKeywordAnalytics(30),
        waApi.getUnmatchedQueries(30, 50),
      ]);
      if (analyticsData) setKeywordStats(analyticsData);
      if (unmatchedData?.unmatchedQueries) setUnmatchedQueries(unmatchedData.unmatchedQueries);
    } catch (e) {
      console.warn("Failed to load keyword intelligence", e);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    if (subTab === 4) {
      loadKeywordIntelligence();
    }
  }, [subTab]);

  const saveAll = async () => {
    try {
      await waApi.updateSettings({
        greeting: { enabled: greeting.on, trigger: greeting.trigger, days: Number(greeting.days) || 14, text: greeting.text, buttons: greeting.buttons || [] },
        away: { enabled: away.on, text: away.text, buttons: away.buttons || [] },
        renewal: { enabled: renewal.on, template: renewal.template },
        quick_replies: quickReplies,
        keyword_rules: rules.map(({ keywords, reply, buttons, match_mode, cooldown_hours, action_tags, active, hit_count, last_hit_at }) => ({
          keywords,
          reply,
          buttons: buttons || [],
          match_mode: match_mode || 'word',
          cooldown_hours: Number(cooldown_hours) || 12,
          action_tags: Array.isArray(action_tags) ? action_tags : (typeof action_tags === 'string' ? action_tags.split(',').map((t) => t.trim()).filter(Boolean) : []),
          active: active !== false,
          hit_count: hit_count || 0,
          last_hit_at: last_hit_at || null,
        })),
        lifecycle: {
          enabled: lifecycle.on,
          follow_up_after_days: Number(lifecycle.days) || 2,
          call_slots: lifecycle.slots.split(',').map((s) => s.trim()).filter(Boolean),
          call_schedule_text: lifecycle.text,
          call_confirm_text: lifecycle.confirmText,
        },
        onboarding: {
          enabled: onboarding.on,
          cooldown_days: Number(onboarding.cooldownDays) || 2,
          max_nudges_per_stage: Number(onboarding.maxNudgesPerStage) || 3,
          max_per_run: Number(onboarding.maxPerRun) || 50,
          fatigue_window_hours: Number(onboarding.fatigueWindowHours) || 24,
          unverified_template: onboarding.unverifiedTemplate || '',
          business_template: onboarding.businessTemplate || '',
          products_template: onboarding.productsTemplate || '',
          plan_template: onboarding.planTemplate || '',
        },
        // Always anchor start_step to the first step so the entry point stays valid.
        flows: flows.map((f) => ({ ...f, start_step: f.steps?.[0]?.id || f.start_step || '' })),
      });
      baselineRef.current = snapshot; // current state is now the saved baseline
      setToast('Automation settings saved');
    } catch (e) {
      setToast('Could not save automation settings. Please try again.');
    }
  };

  const loadAutoStatus = async () => {
    setLoadingAutoStatus(true);
    try {
      const data = await waApi.getFollowUpAutomationStatus();
      if (data) {
        setAutoStatus(data);
        if (data.recent_logs) setAutoLogs(data.recent_logs);
      }
    } catch {
      // Backend status read optional
    } finally {
      setLoadingAutoStatus(false);
    }
  };

  useEffect(() => {
    if (subTab === 2) {
      loadAutoStatus();
    }
  }, [subTab]);

  const handleRunAutomation = async () => {
    setRunningAuto(true);
    try {
      const { report } = await waApi.runFollowUpAutomation({
        segment: runSegment,
        dry_run: runDryRun,
        limit: Number(runLimit) || 50,
      });
      setRunReport(report);
      loadAutoStatus();
      setToast(`Automation run complete: ${report.sent + report.simulated} processed.`);
    } catch (e) {
      setToast(e?.response?.data?.msg || 'Could not run follow-up automation.');
    } finally {
      setRunningAuto(false);
    }
  };

  const handleSendTestMessage = async () => {
    if (!testPhone.trim()) {
      setTestFeedback({ severity: 'error', message: 'Phone number is required.' });
      return;
    }
    if (!testTemplate) {
      setTestFeedback({ severity: 'error', message: 'Template name is required.' });
      return;
    }
    setTestingMsg(true);
    setTestFeedback(null);
    try {
      const { result } = await waApi.testFollowUpMessage({
        phone: testPhone.trim(),
        template_name: testTemplate,
        segment: testSegment,
        sample_name: testSampleName.trim(),
      });
      setTestFeedback({
        severity: 'success',
        message: `Test message ${result.simulated ? 'simulated' : 'sent'} to ${result.phone}!`,
      });
      loadAutoStatus();
    } catch (e) {
      setTestFeedback({
        severity: 'error',
        message: e?.response?.data?.msg || 'Failed to send test message.',
      });
    } finally {
      setTestingMsg(false);
    }
  };

  return (
    <Box>
      <WaSectionHeader
        title="Auto Messages"
        subtitle="Automatic replies and proactive nudges. In-window auto-replies are free-form; proactive sends use approved templates."
        action={
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.5} alignItems="center" justifyContent={{ xs: 'flex-start', sm: 'flex-end' }} sx={{ width: { xs: '100%', sm: 'auto' }, '& > button': { width: { xs: '100%', sm: 'auto' } } }}>
            {dirty && <Chip label="Unsaved changes" size="small" color="warning" variant="outlined" sx={{ fontWeight: 700 }} />}
            <Button variant="contained" onClick={saveAll} disabled={!dirty}>Save Changes</Button>
          </Stack>
        }
      />

      <Box sx={{
        borderBottom: 1,
        borderColor: 'divider',
        mb: 3,
        bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.4) : alpha('#ffffff', 0.6),
        backdropFilter: 'blur(10px)',
        borderRadius: 2.5,
        px: 1,
        border: `1px solid ${alpha(theme.palette.divider, 0.75)}`,
      }}>
        <Tabs
          value={subTab}
          onChange={(e, v) => setSubTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{ sx: { bgcolor: theme.palette.primary.main, height: 3, borderRadius: 3 } }}
          sx={{
            minHeight: 44,
            '& .MuiTabs-scrollButtons': {
              color: 'primary.main',
              borderRadius: 1,
              width: 32,
            },
            '& .MuiTab-root': {
              minHeight: 44,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: { xs: 12, sm: 13.5 },
              fontFamily: 'Inter, sans-serif',
              color: 'text.secondary',
              px: { xs: 1.5, sm: 2.5 },
              py: 1,
              transition: 'all 0.2s ease',
              '&:hover': {
                color: 'text.primary',
                bgcolor: alpha(theme.palette.action.hover, 0.5),
                borderRadius: 2,
              },
              '&.Mui-selected': {
                color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.primary',
                fontWeight: 800,
              }
            }
          }}
        >
          <Tab label="Interactive Replies" />
          <Tab label="Keyword & Quick Replies" />
          <Tab label="Nudges & Funnel Flow" />
          <Tab label="Onboarding Flow" />
          <Tab label="Keyword Intelligence" icon={<Sparkles size={15} style={{ marginRight: 4 }} />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Tab 0: Interactive Replies */}
      {subTab === 0 && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }} sx={waGridItem}>
            <ToggleCard icon={MessageSquare} color={theme.palette.success.main} title="Greeting message"
              desc="Automatically greet customers — on their first message, or after a period of inactivity." enabled={greeting.on}
              onToggle={(e) => setGreeting((g) => ({ ...g, on: e.target.checked }))}>
              <Stack spacing={1.5}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField select fullWidth size="small" label="Send when" value={greeting.trigger}
                    onChange={(e) => setGreeting((g) => ({ ...g, trigger: e.target.value }))}>
                    <MenuItem value="new">New customer's first message</MenuItem>
                    <MenuItem value="inactive">After inactivity</MenuItem>
                    <MenuItem value="always">Every new conversation</MenuItem>
                  </TextField>
                  {greeting.trigger === 'inactive' && (
                    <TextField type="number" size="small" label="Days" sx={{ width: { xs: '100%', sm: 110 } }} value={greeting.days}
                      onChange={(e) => setGreeting((g) => ({ ...g, days: e.target.value }))} />
                  )}
                </Stack>
                <WaMessageEditor value={greeting.text} minRows={2}
                  placeholder="Greeting message — use *bold*, _italic_, ~strike~"
                  onChange={(v) => setGreeting((g) => ({ ...g, text: v }))} />
                <WaReplyButtonsEditor buttons={greeting.buttons} onChange={(b) => setGreeting((g) => ({ ...g, buttons: b }))} />
              </Stack>
            </ToggleCard>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }} sx={waGridItem}>
            <ToggleCard icon={Clock} color={theme.palette.warning.main} title="Away message"
              desc="Sent when a message arrives outside your business hours." enabled={away.on}
              onToggle={(e) => setAway((a) => ({ ...a, on: e.target.checked }))}>
              <Stack spacing={1.5}>
                <WaMessageEditor value={away.text} minRows={2}
                  placeholder="Away message — use *bold*, _italic_, ~strike~"
                  onChange={(v) => setAway((a) => ({ ...a, text: v }))} />
                <WaReplyButtonsEditor buttons={away.buttons} onChange={(b) => setAway((a) => ({ ...a, buttons: b }))} />
              </Stack>
            </ToggleCard>
          </Grid>
        </Grid>
      )}

      {/* Tab 1: Keyword & Quick Replies */}
      {subTab === 1 && (
        <Grid container spacing={2}>
          {/* Keyword auto-reply rules */}
          <Grid size={{ xs: 12, md: 6 }} sx={waGridItem}>
            <Card sx={{ p: 2 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Zap size={19} color={theme.palette.info.main} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 800 }}>Keyword auto-replies</Typography>
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Auto-respond when a message contains any of the keywords.</Typography>
                </Box>
              </Stack>
              <Stack spacing={2}>
                {rules.map((r) => (
                  <Box key={r.id} sx={{
                    p: 2,
                    borderRadius: 2.5,
                    bgcolor: editingId === r.id
                      ? alpha(theme.palette.info.main, 0.08)
                      : theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.8) : '#f8fafc',
                    border: `1px solid ${editingId === r.id
                        ? alpha(theme.palette.info.main, 0.4)
                        : theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.5) : '#e2e8f0'
                      }`,
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: theme.shadows[1],
                      borderColor: editingId === r.id ? alpha(theme.palette.info.main, 0.6) : 'text.secondary',
                    }
                  }}>
                    {/* Actions in top-right */}
                    <Stack direction="row" spacing={0.5} sx={{ position: 'absolute', top: 12, right: 12 }}>
                      <IconButton size="small" aria-label={`Edit rule for ${r.keywords.join(', ')}`}
                        onClick={() => {
                          setEditingId(r.id);
                          setNewRule({
                            keywords: r.keywords.join(', '),
                            reply: r.reply,
                            buttons: r.buttons || [],
                            match_mode: r.match_mode || 'word',
                            cooldown_hours: r.cooldown_hours ?? 12,
                            action_tags: Array.isArray(r.action_tags) ? r.action_tags.join(', ') : '',
                          });
                        }}
                        sx={{ color: 'info.main', bgcolor: alpha(theme.palette.info.main, 0.08), '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.16) } }}>
                        <Pencil size={14} />
                      </IconButton>
                      <IconButton size="small" aria-label={`Delete rule for ${r.keywords.join(', ')}`}
                        onClick={() => { setRules((p) => p.filter((x) => x.id !== r.id)); if (editingId === r.id) { setEditingId(null); setNewRule({ keywords: '', reply: '', buttons: [], match_mode: 'word', cooldown_hours: 12, action_tags: '' }); } }}
                        sx={{ color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.08), '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.16) } }}>
                        <Trash2 size={14} />
                      </IconButton>
                    </Stack>

                    {/* Header/Triggers & Badges */}
                    <Box sx={{ pr: 8, mb: 1.5 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                        <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: 'Inter, sans-serif' }}>
                          If message matches
                        </Typography>
                        <Chip size="small" label={r.match_mode === 'exact' ? 'Exact' : r.match_mode === 'contains' ? 'Contains' : 'Word (\\b)'}
                          sx={{ fontSize: 10, height: 20, fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main' }} />
                        <Chip size="small" label={`⏱️ ${r.cooldown_hours ?? 12}h`}
                          sx={{ fontSize: 10, height: 20, fontWeight: 700, bgcolor: alpha(theme.palette.text.secondary, 0.1) }} />
                        {Number(r.hit_count) > 0 && (
                          <Chip size="small" label={`🔥 ${r.hit_count} hits`}
                            sx={{ fontSize: 10, height: 20, fontWeight: 800, bgcolor: alpha(theme.palette.success.main, 0.12), color: 'success.main' }} />
                        )}
                      </Stack>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {r.keywords.map((k) => (
                          <Chip key={k} label={k} size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.info.main, 0.15) : alpha(theme.palette.info.main, 0.08),
                              color: theme.palette.info.main,
                              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                              borderRadius: 1.5,
                            }}
                          />
                        ))}
                        {Array.isArray(r.action_tags) && r.action_tags.map((t) => (
                          <Chip key={t} label={`🏷️ ${t}`} size="small" variant="outlined"
                            sx={{ fontSize: 11, fontWeight: 600, borderColor: alpha(theme.palette.info.main, 0.3) }} />
                        ))}
                      </Box>
                    </Box>

                    {/* Reply preview */}
                    <Box>
                      <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75, fontFamily: 'Inter, sans-serif' }}>
                        Auto-reply with
                      </Typography>

                      {/* Simulated WhatsApp bubble for the reply */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1
                      }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          bgcolor: alpha(theme.palette.info.main, 0.12),
                          color: theme.palette.info.main,
                          flexShrink: 0,
                          mt: 0.5
                        }}>
                          <Bot size={13} />
                        </Box>
                        <Box sx={{
                          flex: 1,
                          p: 1.5,
                          borderRadius: '12px',
                          borderTopLeftRadius: '2px',
                          bgcolor: theme.palette.mode === 'dark' ? '#202c33' : '#ffffff',
                          border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.4) : alpha(theme.palette.divider, 0.65)}`,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                          maxWidth: '92%'
                        }}>
                          <Typography sx={{
                            fontSize: 13,
                            lineHeight: 1.5,
                            color: 'text.primary',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontFamily: 'Inter, sans-serif'
                          }}>
                            {renderRichText(r.reply)}
                          </Typography>
                          {Array.isArray(r.buttons) && r.buttons.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, pt: 1, borderTop: `1px dashed ${alpha(theme.palette.divider, 0.6)}` }}>
                              {r.buttons.filter((b) => (b.title || '').trim()).map((b, bi) => (
                                <Chip key={bi} size="small" label={b.title}
                                  icon={b.type === 'url' ? <span style={{ fontSize: 11, marginLeft: 6 }}>🔗</span> : undefined}
                                  sx={{ fontWeight: 700, fontSize: 11, bgcolor: alpha(theme.palette.primary.main, 0.08), color: 'primary.main', border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}` }} />
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Stack>
              <Box sx={{ mt: 1.5 }}>
                <TextField size="small" fullWidth label="Keywords" placeholder="e.g. plans, price, promotion" value={newRule.keywords} sx={{ mb: 1.25 }}
                  helperText="Separate multiple keywords with commas — any match triggers this rule."
                  onChange={(e) => setNewRule((r) => ({ ...r, keywords: e.target.value }))} />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mb: 1.25 }}>
                  <TextField select size="small" label="Match Mode" value={newRule.match_mode || 'word'} sx={{ minWidth: 160 }}
                    onChange={(e) => setNewRule((r) => ({ ...r, match_mode: e.target.value }))}>
                    <MenuItem value="word">Whole Word (\b) [Smart]</MenuItem>
                    <MenuItem value="exact">Exact Match</MenuItem>
                    <MenuItem value="contains">Contains Substring</MenuItem>
                  </TextField>
                  <TextField type="number" size="small" label="Cooldown (Hours)" value={newRule.cooldown_hours ?? 12} sx={{ width: { xs: '100%', sm: 140 } }}
                    helperText="Anti-spam window"
                    onChange={(e) => setNewRule((r) => ({ ...r, cooldown_hours: e.target.value }))} />
                  <TextField size="small" fullWidth label="Auto-apply Tags (comma-separated)" placeholder="e.g. Pricing Lead, Hot" value={newRule.action_tags || ''}
                    onChange={(e) => setNewRule((r) => ({ ...r, action_tags: e.target.value }))} />
                </Stack>

                <WaMessageEditor label="Auto-reply" value={newRule.reply}
                  placeholder="Auto-reply text — use *bold*, _italic_, ~strike~"
                  onChange={(v) => setNewRule((r) => ({ ...r, reply: v }))} />
                <WaReplyButtonsEditor buttons={newRule.buttons} onChange={(b) => setNewRule((r) => ({ ...r, buttons: b }))} />
                <Stack direction="row" flexWrap="wrap" justifyContent="flex-end" spacing={1} sx={{ mt: 1 }}>
                  {editingId && (
                    <Button size="small" color="inherit" startIcon={<X size={16} />}
                      onClick={() => { setEditingId(null); setNewRule({ keywords: '', reply: '', buttons: [], match_mode: 'word', cooldown_hours: 12, action_tags: '' }); }}>
                      Cancel
                    </Button>
                  )}
                  <Button size="small" variant="contained" startIcon={editingId ? <Pencil size={15} /> : <Plus size={16} />}
                    disabled={!newRule.keywords.trim() || !newRule.reply.trim()}
                    onClick={() => {
                      const keywords = newRule.keywords.split(',').map((k) => k.trim()).filter(Boolean);
                      if (!keywords.length) return;
                      const reply = newRule.reply.trim();
                      const buttons = (newRule.buttons || []).filter((b) => (b.title || '').trim());
                      const match_mode = newRule.match_mode || 'word';
                      const cooldown_hours = Number(newRule.cooldown_hours) || 12;
                      const action_tags = newRule.action_tags ? newRule.action_tags.split(',').map((t) => t.trim()).filter(Boolean) : [];

                      if (editingId) {
                        setRules((p) => p.map((x) => (x.id === editingId ? { ...x, keywords, reply, buttons, match_mode, cooldown_hours, action_tags } : x)));
                        setEditingId(null);
                      } else {
                        setRules((p) => [...p, { id: `r-${Date.now()}`, keywords, reply, buttons, match_mode, cooldown_hours, action_tags, hit_count: 0 }]);
                      }
                      setNewRule({ keywords: '', reply: '', buttons: [], match_mode: 'word', cooldown_hours: 12, action_tags: '' });
                    }}>
                    {editingId ? 'Update rule' : 'Add rule'}
                  </Button>
                </Stack>
              </Box>
            </Card>
          </Grid>

          {/* Quick replies */}
          <Grid size={{ xs: 12, md: 6 }} sx={waGridItem}>
            <Card sx={{ p: 2 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 800, mb: 0.25 }}>Quick replies</Typography>
              <Stack spacing={1}>
                {quickReplies.map((q, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="flex-start" sx={{ p: 1, borderRadius: 2, bgcolor: theme.palette.background.neutral }}>
                    <Typography sx={{ fontSize: 12.5, flex: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{renderRichText(q)}</Typography>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton size="small" aria-label="Edit quick reply" onClick={() => { setEditingQrIndex(i); setNewQr(q); }} sx={{ color: 'info.main' }}><Pencil size={14} /></IconButton>
                      <IconButton size="small" aria-label="Delete quick reply" onClick={() => { setQuickReplies((p) => p.filter((_, idx) => idx !== i)); if (editingQrIndex === i) { setEditingQrIndex(null); setNewQr(''); } }} sx={{ color: 'error.main' }}><Trash2 size={14} /></IconButton>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
              <Box sx={{ mt: 1.5 }}>
                <WaMessageEditor label={editingQrIndex !== null ? "Edit quick reply" : "New quick reply"} value={newQr}
                  placeholder="Add a quick reply — use *bold*, _italic_, ~strike~"
                  onChange={setNewQr} />
                <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 1 }}>
                  {editingQrIndex !== null && (
                    <Button size="small" color="inherit" startIcon={<X size={16} />}
                      onClick={() => { setEditingQrIndex(null); setNewQr(''); }}>
                      Cancel
                    </Button>
                  )}
                  <Button size="small" variant="contained" startIcon={editingQrIndex !== null ? <Pencil size={15} /> : <Plus size={16} />} disabled={!newQr.trim()}
                    onClick={() => {
                      if (editingQrIndex !== null) {
                        setQuickReplies((p) => p.map((x, idx) => idx === editingQrIndex ? newQr.trim() : x));
                        setEditingQrIndex(null);
                      } else {
                        setQuickReplies((p) => [...p, newQr.trim()]);
                      }
                      setNewQr('');
                    }}>
                    {editingQrIndex !== null ? 'Update reply' : 'Add reply'}
                  </Button>
                </Stack>
              </Box>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tab 2: Nudges & Follow-up Funnel */}
      {subTab === 2 && (
        <Stack spacing={2.5}>
          {/* Audience Pipeline & Action Summary Card */}
          <Card sx={{ p: 2.5, borderRadius: 2.5, border: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    WhatsApp Follow-up Automation Pipeline
                  </Typography>
                  <Chip
                    size="small"
                    label={autoStatus.is_live_send ? 'Meta Live Mode' : 'Simulated / Safe Mode'}
                    color={autoStatus.is_live_send ? 'success' : 'info'}
                    sx={{ fontWeight: 700, height: 22 }}
                  />
                  {onboarding.on ? (
                    <Chip size="small" label="Automation Active" color="success" sx={{ fontWeight: 700, height: 22 }} />
                  ) : (
                    <Chip size="small" label="Automation Paused" sx={{ fontWeight: 700, height: 22 }} />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Targeted, automated WhatsApp templates for <strong>Unverified Users</strong>, <strong>Pending Business</strong>, and <strong>Pending Products</strong>.
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Send size={15} />}
                  onClick={() => {
                    setTestFeedback(null);
                    setTestTemplate(onboarding.businessTemplate || approved[0]?.name || '');
                    setTestModalOpen(true);
                  }}
                  sx={{ fontWeight: 600, borderRadius: 2 }}
                >
                  Send Test Message
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<Play size={15} />}
                  onClick={() => {
                    setRunReport(null);
                    setRunModalOpen(true);
                  }}
                  sx={{ fontWeight: 700, borderRadius: 2 }}
                >
                  Run Follow-up Now
                </Button>
                <Tooltip title="Refresh Audience Data">
                  <IconButton size="small" onClick={loadAutoStatus} disabled={loadingAutoStatus}>
                    <RefreshCw size={17} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            <Divider sx={{ my: 2 }} />

            {/* Live Target Audience Badges */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.error.main, 0.04), borderColor: alpha(theme.palette.error.main, 0.25) }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'error.main', textTransform: 'uppercase' }}>
                    1. Unverified Users
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: 'error.dark' }}>
                    {autoStatus.counts?.unverified || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Pending OTP & phone verification
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 6, sm: 3 }}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.04), borderColor: alpha(theme.palette.warning.main, 0.25) }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.dark', textTransform: 'uppercase' }}>
                    2. Pending Business
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: 'warning.dark' }}>
                    {autoStatus.counts?.business_pending || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Verified sellers with no business
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 6, sm: 3 }}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.04), borderColor: alpha(theme.palette.info.main, 0.25) }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'info.dark', textTransform: 'uppercase' }}>
                    3. Pending Product
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: 'info.dark' }}>
                    {autoStatus.counts?.product_pending || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Business created, 0 catalog items
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 6, sm: 3 }}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.04), borderColor: alpha(theme.palette.success.main, 0.25) }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'success.dark', textTransform: 'uppercase' }}>
                    Total Audience
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: 'success.dark' }}>
                    {autoStatus.counts?.total || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Leads in follow-up pipeline
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Card>

          {/* Configuration Cards Grid */}
          <Grid container spacing={2}>
            {/* Follow-up Templates by Segment */}
            <Grid size={{ xs: 12, md: 6 }} sx={waGridItem}>
              <ToggleCard
                icon={Rocket}
                color={theme.palette.info.main}
                title="Follow-up Message Templates"
                desc="Map approved WhatsApp templates to each segment. Messages are automatically sent with personalized variables (name, business, city)."
                enabled={onboarding.on}
                onToggle={(e) => setOnboarding((o) => ({ ...o, on: e.target.checked }))}
              >
                <Stack spacing={1.75}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="1. Unverified Users Template"
                    value={onboarding.unverifiedTemplate}
                    onChange={(e) => setOnboarding((o) => ({ ...o, unverifiedTemplate: e.target.value }))}
                    helperText="Sent to users who signed up but haven't verified"
                  >
                    <MenuItem value="">— none (skip unverified) —</MenuItem>
                    {approved.map((t) => (
                      <MenuItem key={t.id} value={t.name}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="2. Pending Business Setup Template"
                    value={onboarding.businessTemplate}
                    onChange={(e) => setOnboarding((o) => ({ ...o, businessTemplate: e.target.value }))}
                    helperText="Sent to verified sellers with no business profile"
                  >
                    <MenuItem value="">— none (skip business pending) —</MenuItem>
                    {approved.map((t) => (
                      <MenuItem key={t.id} value={t.name}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="3. Pending Product Catalog Template"
                    value={onboarding.productsTemplate}
                    onChange={(e) => setOnboarding((o) => ({ ...o, productsTemplate: e.target.value }))}
                    helperText="Sent to sellers who created business but 0 products"
                  >
                    <MenuItem value="">— none (skip product pending) —</MenuItem>
                    {approved.map((t) => (
                      <MenuItem key={t.id} value={t.name}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="4. Optional Plan / Leads Nudge Template"
                    value={onboarding.planTemplate}
                    onChange={(e) => setOnboarding((o) => ({ ...o, planTemplate: e.target.value }))}
                    helperText="Sent to active sellers without an active subscription plan"
                  >
                    <MenuItem value="">— none (skip plan nudge) —</MenuItem>
                    {approved.map((t) => (
                      <MenuItem key={t.id} value={t.name}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </ToggleCard>
            </Grid>

            {/* Anti-Spam & Delivery Safeguards */}
            <Grid size={{ xs: 12, md: 6 }} sx={waGridItem}>
              <Card sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ShieldCheck size={20} color={theme.palette.success.main} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 800 }}>Anti-Spam & Delivery Safeguards</Typography>
                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Guarantees clean data and protects WhatsApp Quality Rating</Typography>
                  </Box>
                </Stack>

                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      type="number"
                      size="small"
                      fullWidth
                      label="Cooldown (Days)"
                      value={onboarding.cooldownDays}
                      onChange={(e) => setOnboarding((o) => ({ ...o, cooldownDays: e.target.value }))}
                      helperText="Min days before nudging same user again"
                    />
                    <TextField
                      type="number"
                      size="small"
                      fullWidth
                      label="Max Nudges / Segment"
                      value={onboarding.maxNudgesPerStage ?? 3}
                      onChange={(e) => setOnboarding((o) => ({ ...o, maxNudgesPerStage: e.target.value }))}
                      helperText="Stops after N nudges and tags for ops"
                    />
                  </Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      type="number"
                      size="small"
                      fullWidth
                      label="Anti-Fatigue Window (Hours)"
                      value={onboarding.fatigueWindowHours ?? 24}
                      onChange={(e) => setOnboarding((o) => ({ ...o, fatigueWindowHours: e.target.value }))}
                      helperText="Min hours between outbound messages"
                    />
                    <TextField
                      type="number"
                      size="small"
                      fullWidth
                      label="Batch Limit per Run"
                      value={onboarding.maxPerRun}
                      onChange={(e) => setOnboarding((o) => ({ ...o, maxPerRun: e.target.value }))}
                      helperText="Protects messaging pace per execution"
                    />
                  </Stack>

                  <Alert severity="info" sx={{ borderRadius: 2, fontSize: '0.8125rem' }}>
                    <strong>Clean Data Guarantee:</strong> Automatically standardizes phone numbers (E.164), respects customer opt-out keywords (STOP), and syncs all touches with Follow-Up CRM.
                  </Alert>
                </Stack>
              </Card>
            </Grid>

            {/* Customer lifecycle funnel */}
            <Grid size={{ xs: 12, md: 6 }} sx={waGridItem}>
              <ToggleCard
                icon={GitBranch}
                color={theme.palette.secondary?.main || theme.palette.primary.main}
                title="Customer lifecycle funnel"
                desc="Auto-tag customers Open → Call Pending → Follow Up, and sync Verified / Business / Products tags from their account."
                enabled={lifecycle.on}
                onToggle={(e) => setLifecycle((l) => ({ ...l, on: e.target.checked }))}
              >
                <Stack spacing={1.5}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <TextField type="number" size="small" label="Follow Up after (days)" sx={{ width: { xs: '100%', sm: 170 } }} value={lifecycle.days}
                      onChange={(e) => setLifecycle((l) => ({ ...l, days: e.target.value }))} />
                    <TextField size="small" fullWidth label="Call time options (comma-separated)" value={lifecycle.slots}
                      onChange={(e) => setLifecycle((l) => ({ ...l, slots: e.target.value }))} />
                  </Stack>
                  <TextField fullWidth size="small" label="Call-schedule prompt" multiline minRows={2} value={lifecycle.text}
                    onChange={(e) => setLifecycle((l) => ({ ...l, text: e.target.value }))} />
                  <TextField fullWidth size="small" label="Confirmation after they pick a time" multiline minRows={2} value={lifecycle.confirmText}
                    onChange={(e) => setLifecycle((l) => ({ ...l, confirmText: e.target.value }))}
                    helperText="Auto-sent when a contact replies 11am / 4pm. Use {{time}} for their chosen slot and {{user}} for their name." />
                </Stack>
              </ToggleCard>
            </Grid>

            {/* Subscription renewal reminder */}
            <Grid size={{ xs: 12, md: 6 }} sx={waGridItem}>
              <ToggleCard
                icon={Bot}
                color={theme.palette.primary.main}
                title="Subscription renewal reminder"
                desc="Proactively remind sellers before their plan expires. Requires an approved template."
                enabled={renewal.on}
                onToggle={(e) => setRenewal((r) => ({ ...r, on: e.target.checked }))}
              >
                <TextField select fullWidth size="small" label="Template" value={renewal.template}
                  onChange={(e) => setRenewal((r) => ({ ...r, template: e.target.value }))}>
                  {approved.map((t) => <MenuItem key={t.id} value={t.name}>{t.name}</MenuItem>)}
                </TextField>
              </ToggleCard>
            </Grid>
          </Grid>

          {/* Recent Automation Activity & Execution Logs */}
          <Card sx={{ p: 2, borderRadius: 2.5, border: 1, borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Recent Automation Activity & Delivery Logs
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Complete audit trail of every WhatsApp follow-up attempt, delivery status, and reason
                </Typography>
              </Box>
              <Button size="small" startIcon={<RefreshCw size={14} />} onClick={loadAutoStatus}>
                Refresh Logs
              </Button>
            </Stack>

            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: alpha(theme.palette.grey[500], 0.06) }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Recipient / User</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Segment</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Template</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Attempt</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {autoLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        No automation logs recorded yet. Run a follow-up campaign or send a test above.
                      </TableCell>
                    </TableRow>
                  ) : (
                    autoLogs.map((log) => {
                      const isSent = log.status === 'sent';
                      const isSim = log.status === 'simulated';
                      const isSkip = log.status === 'skipped';
                      const color = isSent ? 'success' : isSim ? 'info' : isSkip ? 'warning' : 'error';

                      return (
                        <TableRow key={log._id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{log.user_name || '—'}</TableCell>
                          <TableCell>{log.phone}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={log.segment?.replace('_', ' ')}
                              sx={{ textTransform: 'capitalize', fontWeight: 600, height: 20, fontSize: '0.6875rem' }}
                            />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 180 }}>
                            <Typography variant="caption" noWrap sx={{ display: 'block' }}>
                              {log.template_name || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={log.status}
                              color={color}
                              sx={{ textTransform: 'uppercase', fontWeight: 700, height: 20, fontSize: '0.6875rem' }}
                            />
                            {log.reason && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.6875rem' }}>
                                {log.reason}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{log.attempt || 1}</TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(log.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Stack>
      )}

      {/* Tab 3: Onboarding Flow (stateful, button-driven funnel) */}
      {subTab === 3 && (
        <WhatsAppFlowBuilder flows={flows} setFlows={setFlows} />
      )}

      {/* Tab 4: Keyword Intelligence & Unmatched Queries */}
      {subTab === 4 && (
        <Stack spacing={2.5}>
          {/* Stat Cards */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={waGridItem}>
              <Card sx={{ p: 2, borderRadius: 2.5 }}>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Inbound Queries</Typography>
                <Typography sx={{ fontSize: 26, fontWeight: 900, mt: 0.5, color: 'text.primary' }}>{keywordStats.totals?.totalQueries || 0}</Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>Last 30 days customer queries</Typography>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={waGridItem}>
              <Card sx={{ p: 2, borderRadius: 2.5 }}>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>Matched & Replied</Typography>
                <Typography sx={{ fontSize: 26, fontWeight: 900, mt: 0.5, color: 'success.main' }}>{keywordStats.totals?.matchedQueries || 0}</Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>Handled by smart keyword rules</Typography>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={waGridItem}>
              <Card sx={{ p: 2, borderRadius: 2.5 }}>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>Unmatched Queries</Typography>
                <Typography sx={{ fontSize: 26, fontWeight: 900, mt: 0.5, color: 'warning.main' }}>{keywordStats.totals?.unmatchedQueries || 0}</Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>Missing intents (handled by AI / agent)</Typography>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={waGridItem}>
              <Card sx={{ p: 2, borderRadius: 2.5 }}>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>Rule Match Rate</Typography>
                <Typography sx={{ fontSize: 26, fontWeight: 900, mt: 0.5, color: 'info.main' }}>{keywordStats.totals?.matchRatePercentage || 0}%</Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>Direct keyword automation rate</Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Top Keywords Breakdown */}
          <Card sx={{ p: 2.5, borderRadius: 2.5 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 800, mb: 0.5 }}>Top Customer Keywords</Typography>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>Most frequent keywords received from WhatsApp users in the last 30 days.</Typography>
            {(!keywordStats.topKeywords || keywordStats.topKeywords.length === 0) ? (
              <Typography sx={{ fontSize: 13, color: 'text.disabled', fontStyle: 'italic' }}>
                No keyword hits logged yet. Inbound customer queries will appear here automatically with hit counters.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {keywordStats.topKeywords.map((k, idx) => (
                  <Chip
                    key={idx}
                    label={`${k.keyword} (${k.hits} hits)`}
                    sx={{
                      fontWeight: 700,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      color: 'primary.main',
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                    }}
                  />
                ))}
              </Box>
            )}
          </Card>

          {/* Unmatched Queries Table */}
          <Card sx={{ p: 2.5, borderRadius: 2.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Box>
                <Typography sx={{ fontSize: 15, fontWeight: 800 }}>Unmatched Inbound Queries (Intent Gaps)</Typography>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                  What customers are asking that doesn't match any rule. Click <b>+ Add as Rule</b> to automate instant responses!
                </Typography>
              </Box>
              <Button size="small" variant="outlined" onClick={loadKeywordIntelligence} disabled={analyticsLoading}>Refresh</Button>
            </Stack>

            {(!unmatchedQueries || unmatchedQueries.length === 0) ? (
              <Box sx={{ p: 3, textAlign: 'center', bgcolor: theme.palette.mode === 'dark' ? alpha('#fff', 0.02) : '#f8fafc', borderRadius: 2 }}>
                <CheckCircle2 size={32} color={theme.palette.success.main} style={{ margin: '0 auto 8px' }} />
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Zero Intent Gaps!</Typography>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>All customer queries are successfully handled by keyword rules or active flows.</Typography>
              </Box>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: 'Inter, sans-serif' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${theme.palette.divider}`, textAlign: 'left' }}>
                      <th style={{ padding: '8px 12px', fontWeight: 800 }}>Customer Query</th>
                      <th style={{ padding: '8px 12px', fontWeight: 800 }}>Hits</th>
                      <th style={{ padding: '8px 12px', fontWeight: 800 }}>Senders</th>
                      <th style={{ padding: '8px 12px', fontWeight: 800 }}>Last Seen</th>
                      <th style={{ padding: '8px 12px', fontWeight: 800, textAlign: 'right' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmatchedQueries.map((item, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
                        <td style={{ padding: '10px 12px', fontWeight: 600 }}>{item.query}</td>
                        <td style={{ padding: '10px 12px' }}><Chip size="small" label={item.count} color="warning" variant="outlined" sx={{ fontWeight: 800 }} /></td>
                        <td style={{ padding: '10px 12px', color: 'text.secondary' }}>{item.uniqueSendersCount} user(s)</td>
                        <td style={{ padding: '10px 12px', color: 'text.secondary' }}>{new Date(item.lastSeen).toLocaleDateString()}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                          <Button size="small" variant="contained" color="primary" startIcon={<Plus size={14} />}
                            onClick={() => {
                              setNewRule({
                                keywords: item.query,
                                reply: '',
                                buttons: [],
                                match_mode: 'word',
                                cooldown_hours: 12,
                                action_tags: '',
                              });
                              setEditingId(null);
                              setSubTab(1); // switch to Keyword & Quick Replies tab!
                            }}>
                            Add as Rule
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            )}
          </Card>
        </Stack>
      )}

      {/* Advanced campaigns details (global bottom note) */}
      <Box sx={{ mt: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
          <Workflow size={18} color={theme.palette.primary.main} />
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
            Advanced, data-driven campaigns (win-backs, lead nudges, SLA alerts) run from the existing <b>Automation Center</b> — enable the WhatsApp channel there to dispatch them.
          </Typography>
        </Stack>
      </Box>

      {/* Dialog: Run Follow-up Automation */}
      <Dialog
        open={runModalOpen}
        onClose={() => !runningAuto && setRunModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Play size={20} color={theme.palette.primary.main} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                Run WhatsApp Follow-up Automation
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Dispatch verified WhatsApp templates to pending & unverified users
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Stack spacing={2.5}>
            <Alert
              severity={autoStatus.is_live_send ? 'warning' : 'info'}
              sx={{ borderRadius: 2 }}
            >
              <strong>{autoStatus.is_live_send ? 'Meta Cloud API Live Mode is ACTIVE.' : 'Safe / Simulation Mode is ACTIVE.'}</strong>{' '}
              {autoStatus.is_live_send
                ? 'Messages will be dispatched to actual user WhatsApp numbers.'
                : 'Simulated messages will be recorded in logs and CRM timelines without deducting Meta balance.'}
            </Alert>

            <TextField
              select
              fullWidth
              size="small"
              label="Target Segment Audience"
              value={runSegment}
              onChange={(e) => setRunSegment(e.target.value)}
              disabled={runningAuto}
            >
              <MenuItem value="all">
                All Funnels Combined ({autoStatus.counts.total} users)
              </MenuItem>
              <MenuItem value="unverified">
                Unverified Users ({autoStatus.counts.unverified} users)
              </MenuItem>
              <MenuItem value="business_pending">
                Pending Business ({autoStatus.counts.business_pending} users)
              </MenuItem>
              <MenuItem value="product_pending">
                Pending Products ({autoStatus.counts.product_pending} users)
              </MenuItem>
            </TextField>

            <TextField
              type="number"
              fullWidth
              size="small"
              label="Batch Limit (Max Contacts to Process)"
              value={runLimit}
              onChange={(e) => setRunLimit(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
              disabled={runningAuto}
              helperText="Recommended 20-100 per run to maintain high WhatsApp quality rating."
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={runDryRun}
                  onChange={(e) => setRunDryRun(e.target.checked)}
                  disabled={runningAuto}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Run in Dry-Run / Test Mode
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Tests user filtering, cooldowns, and anti-fatigue checks without sending live WhatsApp messages.
                  </Typography>
                </Box>
              }
            />

            {runReport && (
              <Card sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.05), border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'success.dark', mb: 1 }}>
                  Execution Result Summary (Run #{runReport.run_id ? runReport.run_id.slice(-6) : 'DONE'})
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">Evaluated</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{runReport.total_candidates}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="success.main">Processed</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'success.main' }}>
                      {runReport.sent + runReport.simulated}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="warning.main">Skipped</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'warning.main' }}>{runReport.skipped}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="error.main">Errors</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'error.main' }}>{runReport.failed}</Typography>
                  </Grid>
                </Grid>
                {runReport.skip_reasons && Object.keys(runReport.skip_reasons).length > 0 && (
                  <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px dashed #ddd' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Guardrail skips: </Typography>
                    {Object.entries(runReport.skip_reasons).map(([k, v]) => (
                      <Chip key={k} size="small" label={`${k}: ${v}`} sx={{ mr: 0.5, mb: 0.5, fontSize: 11 }} />
                    ))}
                  </Box>
                )}
              </Card>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setRunModalOpen(false)}
            disabled={runningAuto}
            color="inherit"
          >
            {runReport ? 'Done' : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRunAutomation}
            disabled={runningAuto}
            startIcon={runningAuto ? <CircularProgress size={16} color="inherit" /> : <Play size={16} />}
            sx={{ fontWeight: 700, px: 3 }}
          >
            {runningAuto ? 'Processing...' : (runDryRun ? 'Start Dry Run' : 'Execute Run')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Send Test Message */}
      <Dialog
        open={testModalOpen}
        onClose={() => !testingMsg && setTestModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Send size={18} color={theme.palette.info.main} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                Send Single Test Message
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Verify template formatting & delivery on a specific mobile number
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Stack spacing={2}>
            {testFeedback && (
              <Alert severity={testFeedback.severity} sx={{ borderRadius: 2 }}>
                {testFeedback.message}
              </Alert>
            )}

            <TextField
              fullWidth
              size="small"
              label="Recipient Mobile Number"
              placeholder="e.g. 9876543210 or +919876543210"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              disabled={testingMsg}
              helperText="E.164 normalization will be applied automatically (91XXXXXXXXXX)"
            />

            <TextField
              select
              fullWidth
              size="small"
              label="Stage Context"
              value={testSegment}
              onChange={(e) => {
                const seg = e.target.value;
                setTestSegment(seg);
                if (seg === 'unverified' && onboarding.unverifiedTemplate) {
                  setTestTemplate(onboarding.unverifiedTemplate);
                } else if (seg === 'business_pending' && onboarding.businessTemplate) {
                  setTestTemplate(onboarding.businessTemplate);
                } else if (seg === 'product_pending' && onboarding.productsTemplate) {
                  setTestTemplate(onboarding.productsTemplate);
                }
              }}
              disabled={testingMsg}
            >
              <MenuItem value="unverified">Unverified User Onboarding</MenuItem>
              <MenuItem value="business_pending">Pending Business Profile</MenuItem>
              <MenuItem value="product_pending">Pending Product Catalog</MenuItem>
            </TextField>

            <TextField
              fullWidth
              size="small"
              label="Meta Approved Template Name"
              value={testTemplate}
              onChange={(e) => setTestTemplate(e.target.value)}
              disabled={testingMsg}
              placeholder="e.g. business_profile_activation_utility"
              helperText="Select or type the Meta template name"
            />

            <TextField
              fullWidth
              size="small"
              label="Sample Customer / Business Name"
              value={testSampleName}
              onChange={(e) => setTestSampleName(e.target.value)}
              disabled={testingMsg}
              placeholder="e.g. Rahul Sharma"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setTestModalOpen(false)}
            disabled={testingMsg}
            color="inherit"
          >
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSendTestMessage}
            disabled={testingMsg || !testPhone.trim() || !testTemplate.trim()}
            startIcon={testingMsg ? <CircularProgress size={16} color="inherit" /> : <Send size={16} />}
            sx={{ fontWeight: 700, px: 3 }}
          >
            {testingMsg ? 'Sending...' : 'Send Test'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={/could not|error|fail/i.test(toast) ? 'error' : 'success'} variant="filled" onClose={() => setToast('')}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default WhatsAppAutomation;
